import { useEffect, useRef } from 'react';
import * as THREE from 'three';

export function WelcomeSplash() {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x050816, 0.004);

    const camera = new THREE.PerspectiveCamera(60, 1, 0.1, 500);
    camera.position.set(0, 0, 140);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const particleCount = 10000;
    const positions = new Float32Array(particleCount * 3);
    const seeds = new Float32Array(particleCount);
    const origins = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);
    const colors = new Float32Array(particleCount * 3);

    const colorA = new THREE.Color('#7dd3fc');
    const colorB = new THREE.Color('#a78bfa');
    const mixed = new THREE.Color();

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      const spread = 48;
      const x = (Math.random() * 2 - 1) * spread;
      const y = (Math.random() * 2 - 1) * spread * 0.8;
      const z = (Math.random() * 2 - 1) * spread;
      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = z;
      origins[i3] = x;
      origins[i3 + 1] = y;
      origins[i3 + 2] = z;
      seeds[i] = Math.random() * Math.PI * 2;
      sizes[i] = 0.4 + Math.random() * 1.2;

      mixed.copy(colorA).lerp(colorB, Math.random());
      colors[i3] = mixed.r;
      colors[i3 + 1] = mixed.g;
      colors[i3 + 2] = mixed.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.PointsMaterial({
      size: 0.18,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const cloud = new THREE.Points(geometry, material);
    cloud.position.z = 10;
    cloud.position.x = 30;
    scene.add(cloud);

    const onResize = () => {
      const { clientWidth, clientHeight } = container;
      if (clientWidth === 0 || clientHeight === 0) return;
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(clientWidth, clientHeight);
    };

    onResize();
    window.addEventListener('resize', onResize);

    const timer = new THREE.Timer();
    let rafId = 0;
    const positionAttribute = geometry.getAttribute('position') as THREE.BufferAttribute;

    const animate = (timestamp?: number) => {
      timer.update(timestamp ?? performance.now());
      const elapsed = timer.getElapsed();

      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        const seed = seeds[i];
        const ox = origins[i3];
        const oy = origins[i3 + 1];
        const oz = origins[i3 + 2];
        positionAttribute.array[i3] = ox + Math.sin(elapsed * 0.38 + seed + oz * 0.02) * 2.6;
        positionAttribute.array[i3 + 1] = oy + Math.cos(elapsed * 0.31 + seed * 1.17 + ox * 0.015) * 2.2;
        positionAttribute.array[i3 + 2] = oz + Math.sin(elapsed * 0.27 + seed * 0.73 + oy * 0.018) * 2.8;
      }

      positionAttribute.needsUpdate = true;
      cloud.rotation.y = elapsed * 0.07;
      cloud.rotation.x = Math.sin(elapsed * 0.15) * 0.08;
      camera.position.z = 100 + Math.sin(elapsed * 0.22) * 2.2;

      renderer.render(scene, camera);
      rafId = window.requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', onResize);

      geometry.dispose();
      material.dispose();
      renderer.dispose();

      if (renderer.domElement.parentElement === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="welcome-splash absolute z-1 top-0 bg-transparent left-0 h-full w-full" />
  );
}